import { useMutation, useQueryClient } from '@tanstack/react-query'

import { putRequest } from 'src/lib/api-client'
import { queries } from 'src/lib/query-keys'

import { useMyUserData } from '../accounts/use-my-user'

import type { LeadCallMethod } from './use-user-feature-preferences'

type NotificationType = 'email' | 'sms' | 'push'

interface UpdateUserTutorialsData {
  feedTutorial?: boolean
  receivedReferralTutorial?: boolean
  sendReferralTutorial?: boolean
  homeTutorial?: boolean
  weeklyRecapSeen?: boolean
}

export interface UpdateUserPreferencesData {
  lynkCompleted?: NotificationType[] | null
  lynkNeedAssistance?: NotificationType[] | null
  lynkHostile?: NotificationType[] | null
  lynkExtremeCase?: NotificationType[] | null
  lynkOffTopic?: NotificationType[] | null
  lynkDisabled?: NotificationType[] | null
  lynkFailed?: NotificationType[] | null
  teamInvitationReceived?: NotificationType[] | null
  teamInvitationInterested?: NotificationType[] | null
  teamInvitationApproved?: NotificationType[] | null
  teamInvitationDeclinedByInvitee?: NotificationType[] | null
  teamInvitationDeclinedByConsumer?: NotificationType[] | null
  teamInvitationAutoRejected?: NotificationType[] | null
  leadInquiryReceived?: NotificationType[] | null
  leadInquiryReminder?: NotificationType[] | null
  referralNewMessage?: NotificationType[] | null
  partnerMatchNewMessage?: NotificationType[] | null
  sneakNewMessage?: NotificationType[] | null
  teamNewMessage?: NotificationType[] | null
  dmNewMessage?: NotificationType[] | null
  feedLikes?: NotificationType[] | null
  feedComments?: NotificationType[] | null
  feedReplies?: NotificationType[] | null
  licenseUpdates?: NotificationType[] | null
  pointsReceived?: NotificationType[] | null
  networkNewConnection?: NotificationType[] | null
  partnerMatchWeeklyReset?: NotificationType[] | null
  weeklyRecap?: NotificationType[] | null

  // Legacy keys kept for compatibility with older settings flows.
  referralReceivedNew?: NotificationType[] | null
  referralReceivedTimeBased?: NotificationType[] | null
  referralReceivedApproved?: NotificationType[] | null
  referralReceivedDenied?: NotificationType[] | null
  referralReceivedExpired?: NotificationType[] | null
  referralSentInterested?: NotificationType[] | null
  referralSentDeclined?: NotificationType[] | null
  referralSentTimeBased?: NotificationType[] | null
  chatNewMessage?: NotificationType[] | null

  postsVisibleToConsumer?: boolean
  transactionsVisibleToConsumer?: boolean
  portfolioVisibleToConsumer?: boolean
  leadCallSummaryEnabled?: boolean
  leadCallMethod?: LeadCallMethod
  autoAcceptLeads?: boolean
}

export type NotificationPreferencesPatch = Record<string, NotificationType[] | null>

async function updateUserTutorials(data: UpdateUserTutorialsData): Promise<void> {
  await putRequest('/users/preferences/update', data)
}

export function useUpdateUserTutorials() {
  const queryClient = useQueryClient()
  const { data: user } = useMyUserData()

  return useMutation({
    mutationFn: updateUserTutorials,
    onSuccess: () => {
      queryClient.invalidateQueries(queries.users.me._ctx.tutorials)
      queryClient.invalidateQueries(queries.users.me._ctx.preferences)
      if (user?.id) {
        queryClient.invalidateQueries(queries.users.me._ctx.privacyPreferences(user.id))
      }
    },
  })
}

async function updateUserPreferences(data: UpdateUserPreferencesData): Promise<void> {
  await putRequest('/users/preferences/update', data)
}

export function useUpdateUserPreferences() {
  const queryClient = useQueryClient()
  const { data: user } = useMyUserData()

  return useMutation({
    mutationFn: updateUserPreferences,
    onSuccess: () => {
      queryClient.invalidateQueries(queries.users.me._ctx.tutorials)
      queryClient.invalidateQueries(queries.users.me._ctx.preferences)
      if (user?.id) {
        queryClient.invalidateQueries(queries.users.me._ctx.privacyPreferences(user.id))
      }
    },
  })
}
